// A layout's title with single words marked (SA, Features 4): bold, italic, underline, strikethrough and a colour on
// the words that carry them, kept as runs beside the title's plain words (`titleRuns`). The plain title stays what
// agents, Markdown, search and the outline read; the runs are kept only while their words are the title's, so a title
// written anew elsewhere (by an agent, in Markdown) simply drops them.

import type { DocMark, DocNode } from "./docblocks";
import { cleanColor } from "./slideDesign";

/** Runs' words, a hard break a line break. */
export function runsText(nodes: DocNode[] | undefined): string {
  return (nodes ?? []).map((n) => (n.type === "hardBreak" ? "\n" : n.type === "text" ? (n.text ?? "") : "")).join("");
}

/** Runs as kept: text with the marks a title takes, and line breaks; undefined when no word is marked. */
export function titleRunsOf(raw: unknown): DocNode[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: DocNode[] = [];
  let marked = false;
  for (const n of raw.slice(0, 400)) {
    if (!n || typeof n !== "object") continue;
    const node = n as DocNode;
    if (node.type === "hardBreak") out.push({ type: "hardBreak" });
    else if (node.type === "text" && typeof node.text === "string" && node.text) {
      const marks: DocMark[] = [];
      for (const m of node.marks ?? []) {
        if (m.type === "bold" || m.type === "italic" || m.type === "underline" || m.type === "strike") marks.push({ type: m.type });
        else if (m.type === "textStyle") {
          const color = cleanColor(m.attrs?.color);
          if (color) marks.push({ type: "textStyle", attrs: { color } });
        }
      }
      if (marks.length) marked = true;
      out.push(marks.length ? { type: "text", text: node.text, marks } : { type: "text", text: node.text });
    }
  }
  return marked ? out : undefined;
}

/** A slide's title runs as kept: only while their words are the title's own. */
export function cleanTitleRuns(raw: unknown, title: string): DocNode[] | undefined {
  const runs = titleRunsOf(raw);
  return runs && runsText(runs) === title ? runs : undefined;
}
